import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { FilterRequest } from './types';

/** Insights date picker rejects a span longer than this many days. */
export const INSIGHTS_MAX_RANGE_DAYS = 92;

const NAME_FIELDS = ['name', 'category', 'dimension'];

/**
 * Chart dimension keys are often the analytics column id (intent, primarytopic).
 * Insights filters use the Data View column name (Intent, PrimaryTopic).
 */
const DIMENSION_TO_INSIGHT_COLUMN: Record<string, string> = {
  intent: 'Intent',
  primarytopic: 'PrimaryTopic',
  queuename: 'Queue_Name',
  queue_name: 'Queue_Name',
  agentusername: 'Agent_Username',
  agent_username: 'Agent_Username',
  rootcause: 'RootCause',
  rootcauseclustered: 'RootCauseClustered',
  customerpainpoint: 'CustomerPainPoint',
  customerpainpointclustered: 'CustomerPainPointClustered',
  outcome: 'Outcome',
  outcomeclustered: 'OutcomeClustered',
  trigger: 'Trigger',
  triggerclustered: 'TriggerClustered',
  callertype: 'CallerType',
  callresolution: 'CallResolution',
  customersentimentatstart: 'CustomerSentimentAtStart',
  customersentimentatend: 'CustomerSentimentAtEnd',
  journeymoment: 'JourneyMoment',
  topics: 'Topics',
  topicsclustered: 'TopicsClustered',
};

const INSIGHT_COLUMN_NAMES = Object.values(DIMENSION_TO_INSIGHT_COLUMN);

/** Same labels the chart badge uses for API duration values. */
const DURATION_TO_RANGE: Record<string, string> = {
  today: 'today',
  dynamic_today: 'today',
  this_week: 'this_week',
  dynamic_daily: 'this_week',
  this_month: 'this_month',
  dynamic_weekly: 'this_month',
  this_quarter: 'this_quarter',
  dynamic_monthly: 'this_quarter',
  this_year: 'this_year',
  dynamic_quarterly: 'this_year',
  last_n_days: 'last_n_days',
  dynamic_last_n_days: 'last_n_days',
  fixed: 'fixed',
  custom_range: 'custom_range',
};

const DYNAMIC_RANGE_TYPES = new Set([
  'today',
  'this_week',
  'this_month',
  'this_quarter',
  'this_year',
  'last_n_days',
]);

export interface ChartQueryContext {
  dimension: string | null;
  secondaryDimension: string | null;
  filters: FilterRequest[];
  /** Explicit custom/fixed dates. Dynamic charts leave these empty and use dateRangeType. */
  startDate: string | null;
  endDate: string | null;
  dateRangeType: string | null;
  lastNDays: number | null;
  timeZone: string | null;
}

export interface ResolvedChartDateRange {
  startDate: string;
  endDate: string;
  /** True when the chart window was longer than Insights allows. */
  capped: boolean;
}

export interface DrilldownOpenOptions {
  /** Set when every selected row shares one secondary-dimension value. */
  secondaryValue?: string | null;
  /** Selected rows used different secondary values, so that breakdown is left off. */
  secondarySkipped?: boolean;
  /**
   * Comparison charts identify a bar by its series period (Jan 2026) and x-axis
   * bucket (week 3). This replaces the chart window with that bar's dates.
   */
  dateRange?: { startDate: string; endDate: string } | null;
  /** The series label is a time period, so it is not added as a dimension filter. */
  skipDimensionFilter?: boolean;
}

export interface ComparisonSlice {
  label: string;
  startDate: string;
  endDate: string;
  /** X-axis label to filter on. Null when the bucket is a week number. */
  dimensionValue: string | null;
}

export type RowDrilldown =
  | { kind: 'value'; value: string; secondary: string | null }
  | { kind: 'period'; slice: ComparisonSlice }
  | { kind: 'empty-period'; label: string };

const MONTH_INDEX: Record<string, number> = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};

export function toInsightColumnName(dimension: string): string {
  const trimmed = dimension.trim();
  if (!trimmed) {
    return trimmed;
  }
  const compact = trimmed.toLowerCase().replace(/[\s_-]+/g, '');
  const underscored = trimmed.toLowerCase().replace(/[\s-]+/g, '_');
  if (DIMENSION_TO_INSIGHT_COLUMN[underscored]) {
    return DIMENSION_TO_INSIGHT_COLUMN[underscored];
  }
  if (DIMENSION_TO_INSIGHT_COLUMN[compact]) {
    return DIMENSION_TO_INSIGHT_COLUMN[compact];
  }
  const canonical = INSIGHT_COLUMN_NAMES.find(
    (column) => column.toLowerCase().replace(/[\s_-]+/g, '') === compact
  );
  return canonical || trimmed;
}

export function chartTableNameValue(row: any): string | null {
  return readRowField(row, NAME_FIELDS);
}

export function chartTableSecondaryValue(row: any): string | null {
  return readRowField(row, ['secondary_dimension']);
}

/**
 * A comparison ("vs") row names the series after a month ("Jan 2026") and puts the
 * x-axis bucket in category (week 1, week 2, ...). Those bars are a date slice,
 * not a dimension value called "Jan 2026".
 */
export function describeRowDrilldown(row: any): RowDrilldown | null {
  const name = readRowField(row, ['name']);
  const category = readRowField(row, ['category']);
  if (name && category && name.toLowerCase() !== category.toLowerCase()) {
    const period = parseMonthPeriod(name);
    if (period) {
      const week = /^\d+$/.test(category) ? Number(category) : null;
      if (week != null) {
        const range = weekOfMonthRange(period.year, period.month, week);
        if (!range) {
          return { kind: 'empty-period', label: `${name}, week ${week}` };
        }
        return {
          kind: 'period',
          slice: {
            label: `${name}, week ${week}`,
            startDate: range.start.format('YYYY-MM-DD'),
            endDate: range.end.format('YYYY-MM-DD'),
            dimensionValue: null,
          },
        };
      }
      const monthStart = dayjs(new Date(period.year, period.month, 1));
      return {
        kind: 'period',
        slice: {
          label: `${name}, ${category}`,
          startDate: monthStart.format('YYYY-MM-DD'),
          endDate: monthStart.endOf('month').format('YYYY-MM-DD'),
          dimensionValue: category,
        },
      };
    }
  }

  const value = chartTableNameValue(row);
  if (!value) {
    return null;
  }
  return { kind: 'value', value, secondary: chartTableSecondaryValue(row) };
}

export function mergePeriodSlices(slices: ComparisonSlice[]): {
  startDate: string;
  endDate: string;
  dimensionValues: string[];
  label: string;
  skipDimensionFilter: boolean;
} {
  const startDate = slices.map((slice) => slice.startDate).sort()[0];
  const endDate = slices.map((slice) => slice.endDate).sort().slice(-1)[0];
  const dimensionValues = uniqueTrimmed(
    slices.map((slice) => slice.dimensionValue).filter((value): value is string => !!value)
  );
  const everyRowHasDimension = dimensionValues.length > 0 && slices.every((slice) => !!slice.dimensionValue);
  return {
    startDate,
    endDate,
    dimensionValues: everyRowHasDimension ? dimensionValues : [],
    label: slices.length === 1 ? slices[0].label : `${slices.length} selected comparison points`,
    skipDimensionFilter: !everyRowHasDimension,
  };
}

const OPERATOR_LABEL: Record<string, string> = {
  contains: 'contains',
  does_not_contain: 'does not contain',
  contains_either_of: 'contains either of',
  is: 'is',
  is_not: 'is not',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  on: 'on',
  before: 'before',
  after: 'after',
  on_or_before: 'on or before',
  on_or_after: 'on or after',
  date_range: 'date range',
  has_any_of: 'has any of',
  has_all_of: 'has all of',
  is_exactly: 'is exactly',
  has_none_of: 'has none of',
};

export interface InsightsConfirmSelection {
  label: string;
  volume?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

export interface InsightsConfirmInput {
  chartTitle?: string | null;
  context: ChartQueryContext;
  values: string[];
  subjectLabel?: string | null;
  dateRange?: { startDate: string; endDate: string } | null;
  skipDimensionFilter?: boolean;
  secondaryValue?: string | null;
  secondarySkipped?: boolean;
  selections?: InsightsConfirmSelection[];
  today?: Dayjs;
}

export function chartTableVolumeLabel(row: any): string | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const countKey = Object.keys(row).find((key) => ['count', 'volume'].includes(key.toLowerCase()));
  if (!countKey) {
    return null;
  }
  const count = Number(row[countKey]);
  if (!Number.isFinite(count)) {
    return null;
  }
  const countText = count.toLocaleString('en-US');
  const percentKey = Object.keys(row).find((key) => key.toLowerCase() === 'percentage');
  if (!percentKey || row[percentKey] == null || row[percentKey] === '') {
    return countText;
  }
  const percent = Number(row[percentKey]);
  return Number.isFinite(percent) ? `${countText} (${percent.toFixed(2)}%)` : countText;
}

export interface InsightsConfirmFilter {
  column: string;
  operator: string;
  value: string;
}

export interface InsightsConfirmBlock {
  label: string;
  primary?: string;
  notes?: string[];
  items?: string[];
  filters?: InsightsConfirmFilter[];
}

/** Date range and filters shown before moving to the Insights Dashboard. */
export function buildInsightsConfirmMessage(input: InsightsConfirmInput): InsightsConfirmBlock[] {
  const selections = (input.selections || []).filter((item) => item.label?.trim());
  const blocks: InsightsConfirmBlock[] = [];
  const timezone = input.context.timeZone?.trim() || 'EST';

  const applied = input.dateRange?.startDate && input.dateRange?.endDate
    ? { startDate: input.dateRange.startDate, endDate: input.dateRange.endDate, capped: false }
    : resolveEffectiveDateRange(input.context, input.today);

  if (applied) {
    const notes: string[] = [];
    const items: string[] = [];
    if (input.skipDimensionFilter && selections.length === 1) {
      notes.push(selections[0].label);
    } else if (input.skipDimensionFilter && selections.length > 1) {
      items.push(...selections.slice(0, 6).map((item) => item.label));
      if (selections.length > 6) {
        notes.push(`and ${selections.length - 6} more`);
      }
      if (selectionRangeHasGaps(selections)) {
        notes.push('Includes the days between the selected weeks.');
      }
    }
    const chartRange = input.dateRange ? resolveEffectiveDateRange(input.context, input.today) : null;
    if (
      chartRange &&
      (chartRange.startDate !== applied.startDate || chartRange.endDate !== applied.endDate)
    ) {
      notes.push(`Chart covers ${formatConfirmRange(chartRange.startDate, chartRange.endDate)}.`);
    }
    if (applied.capped) {
      const source = input.context.dateRangeType === 'last_n_days' && input.context.lastNDays
        ? `the last ${input.context.lastNDays} days`
        : 'the chart range';
      notes.push(`Shortened from ${source} to the latest 92 days.`);
    }
    blocks.push({
      label: 'Date range',
      primary: `${formatConfirmRange(applied.startDate, applied.endDate)} (${timezone})`,
      notes: notes.length ? notes : undefined,
      items: items.length ? items : undefined,
    });
  } else {
    blocks.push({ label: 'Date range', primary: `Not set (${timezone})` });
  }

  const filters = (input.context.filters || []).flatMap(collectConfirmFilters);
  if (!input.skipDimensionFilter && input.context.dimension && input.values.length) {
    filters.push({
      column: formatConfirmColumn(input.context.dimension),
      operator: input.values.length > 1 ? 'contains either of' : 'is',
      value: input.values.join(', '),
    });
  }
  if (input.secondaryValue && input.context.secondaryDimension && !input.secondarySkipped) {
    filters.push({
      column: formatConfirmColumn(input.context.secondaryDimension),
      operator: 'is',
      value: input.secondaryValue,
    });
  }

  blocks.push(filters.length
    ? { label: 'Filters', filters }
    : { label: 'Filters', notes: ['None'] });

  if (input.secondarySkipped && input.context.dimension) {
    blocks.push({
      label: 'Breakdown',
      notes: [`Only ${formatConfirmColumn(input.context.dimension)} is included. Secondary values differ across the selection.`],
    });
  }

  return blocks;
}

function formatConfirmRange(startDate: string, endDate: string): string {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) {
    return `${startDate} – ${endDate}`;
  }
  if (start.isSame(end, 'day')) {
    return start.format('DD MMM YYYY');
  }
  return `${start.format('DD MMM YYYY')} – ${end.format('DD MMM YYYY')}`;
}

function formatConfirmColumn(column: string): string {
  return toInsightColumnName(column)
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2');
}

function collectConfirmFilters(filter: FilterRequest): InsightsConfirmFilter[] {
  if (filter.isClusterFilter && filter.clusterFilters?.length) {
    return filter.clusterFilters.map((cluster) => ({
      column: formatConfirmColumn(cluster.columnName || filter.columnName),
      operator: operatorLabel(cluster.operationType),
      value: formatConfirmValue(cluster.columnValue),
    }));
  }
  return [{
    column: formatConfirmColumn(filter.columnName),
    operator: operatorLabel(filter.operationType),
    value: formatConfirmValue(filter.value),
  }];
}

function operatorLabel(operationType: string): string {
  return OPERATOR_LABEL[operationType] || String(operationType || '').replace(/_/g, ' ');
}

function formatConfirmValue(value: unknown): string {
  const items = (Array.isArray(value) ? value : [value])
    .map((item) => String(item ?? '').trim())
    .filter((item) => !!item && item !== '-');
  if (!items.length) {
    return '';
  }
  return items.join(', ');
}

function selectionRangeHasGaps(selections: InsightsConfirmSelection[]): boolean {
  const ranges = selections
    .filter((item) => item.startDate && item.endDate)
    .map((item) => ({ start: dayjs(item.startDate), end: dayjs(item.endDate) }))
    .filter((item) => item.start.isValid() && item.end.isValid())
    .sort((left, right) => left.start.valueOf() - right.start.valueOf());
  for (let index = 1; index < ranges.length; index += 1) {
    if (ranges[index].start.diff(ranges[index - 1].end, 'day') > 1) {
      return true;
    }
  }
  return false;
}

export function capExplicitDateRange(
  startDate: string,
  endDate: string,
  today: Dayjs = dayjs()
): ResolvedChartDateRange | null {
  return capToInsightsLimit(dayjs(startDate), dayjs(endDate), today);
}

function parseMonthPeriod(label: string): { year: number; month: number } | null {
  const match = label.trim().match(/^([A-Za-z]+)[\s/-]+(\d{4})$/);
  if (!match) {
    return null;
  }
  const month = MONTH_INDEX[match[1].toLowerCase()];
  const year = Number(match[2]);
  if (month === undefined || year < 1900 || year > 2200) {
    return null;
  }
  return { year, month };
}

/**
 * Week buckets that line up across months: Monday-start weeks clipped to the month.
 * January 2026 week 3 is 12–18 Jan. March 2026 has a sixth week (30–31 Mar).
 */
function weekOfMonthRange(year: number, month: number, weekIndex: number): { start: Dayjs; end: Dayjs } | null {
  if (!Number.isInteger(weekIndex) || weekIndex < 1 || weekIndex > 6) {
    return null;
  }
  const monthStart = dayjs(new Date(year, month, 1)).startOf('day');
  const monthEnd = monthStart.endOf('month').startOf('day');
  const weekday = monthStart.day();
  const daysSinceMonday = weekday === 0 ? 6 : weekday - 1;
  const firstMonday = monthStart.subtract(daysSinceMonday, 'day');
  const weekStart = firstMonday.add((weekIndex - 1) * 7, 'day');
  const weekEnd = weekStart.add(6, 'day');
  if (weekStart.isAfter(monthEnd, 'day')) {
    return null;
  }
  const start = weekStart.isBefore(monthStart, 'day') ? monthStart : weekStart;
  const end = weekEnd.isAfter(monthEnd, 'day') ? monthEnd : weekEnd;
  if (start.isAfter(end, 'day')) {
    return null;
  }
  return { start, end };
}

/**
 * One value uses an exact "is" match. Several values use one "contains either of"
 * condition so the chart's existing filters stay AND-ed with the selection.
 */
export function buildDrilldownFilters(
  existing: FilterRequest[],
  dimension: string,
  values: string[],
  secondary?: { column: string; value: string } | null
): FilterRequest[] {
  const uniqueValues = uniqueTrimmed(values);
  const filters = (existing || []).map((filter) => ({
    ...filter,
    logicalOperator: filter.logicalOperator || 'AND',
    isClusterFilter: Boolean(filter.isClusterFilter),
    clusterFilters: Array.isArray(filter.clusterFilters)
      ? filter.clusterFilters.map((cluster) => ({ ...cluster }))
      : [],
  }));

  filters.push({
    logicalOperator: 'AND',
    columnName: toInsightColumnName(dimension),
    operationType: uniqueValues.length > 1 ? 'contains_either_of' : 'is',
    value: uniqueValues.length > 1 ? uniqueValues : uniqueValues[0],
    isClusterFilter: false,
    clusterFilters: [],
  });

  const secondaryColumn = secondary?.column?.trim();
  const secondaryValue = secondary?.value?.trim();
  if (
    secondaryColumn &&
    secondaryValue &&
    toInsightColumnName(secondaryColumn) !== toInsightColumnName(dimension)
  ) {
    filters.push({
      logicalOperator: 'AND',
      columnName: toInsightColumnName(secondaryColumn),
      operationType: 'is',
      value: secondaryValue,
      isClusterFilter: false,
      clusterFilters: [],
    });
  }

  return filters;
}

export function readChartQueryContext(source: any): ChartQueryContext {
  const sources = collectSources(source);
  const criteria = readCriteria(sources);
  const explicitType = firstString([criteria, ...sources], ['date_range_type', 'dateRangeType']);
  const duration = firstString(sources, [
    'DurationBehavior',
    'duration_behavior',
    'durationBehavior',
    'chart_duration_tag',
  ]);
  const dateRangeType = normalizeRangeType(explicitType || duration);

  return {
    dimension: firstString(sources, ['dimension', 'Dimension', 'primary_dimension', 'PrimaryDimension']),
    secondaryDimension: firstString(sources, [
      'secondary_dimension',
      'secondaryDimension',
      'SecondaryDimension',
    ]),
    filters: readFilters(sources),
    startDate: normalizeDateString(firstString(sources, ['start_date', 'startDate', 'StartDate'])),
    endDate: normalizeDateString(firstString(sources, ['end_date', 'endDate', 'EndDate'])),
    dateRangeType,
    lastNDays: readLastNDays(criteria, sources),
    timeZone: firstString(sources, ['time_zone', 'timeZone', 'TimeZone']),
  };
}

export function mergeChartQueryContext(
  local: ChartQueryContext,
  remote: ChartQueryContext
): ChartQueryContext {
  return {
    dimension: remote.dimension || local.dimension,
    secondaryDimension: remote.secondaryDimension || local.secondaryDimension,
    filters: remote.filters.length ? remote.filters : local.filters,
    startDate: remote.startDate || local.startDate,
    endDate: remote.endDate || local.endDate,
    dateRangeType: remote.dateRangeType || local.dateRangeType,
    lastNDays: remote.lastNDays ?? local.lastNDays,
    timeZone: remote.timeZone || local.timeZone,
  };
}

export function chartQueryFieldsFromApi(source: any): {
  dimension?: string;
  secondary_dimension?: string | null;
  filters?: FilterRequest[];
  start_date?: string;
  end_date?: string;
  criteria?: Record<string, unknown>;
  time_zone?: string;
} {
  const context = readChartQueryContext(source);
  const criteria = readCriteria(collectSources(source));
  const fields: {
    dimension?: string;
    secondary_dimension?: string | null;
    filters?: FilterRequest[];
    start_date?: string;
    end_date?: string;
    criteria?: Record<string, unknown>;
    time_zone?: string;
  } = {};

  if (context.dimension) {
    fields.dimension = context.dimension;
  }
  if (context.secondaryDimension) {
    fields.secondary_dimension = context.secondaryDimension;
  }
  if (context.filters.length) {
    fields.filters = context.filters;
  }
  if (context.startDate) {
    fields.start_date = context.startDate;
  }
  if (context.endDate) {
    fields.end_date = context.endDate;
  }
  if (criteria) {
    fields.criteria = criteria;
  }
  if (context.timeZone) {
    fields.time_zone = context.timeZone;
  }
  return fields;
}

export function resolveEffectiveDateRange(
  context: ChartQueryContext,
  today: Dayjs = dayjs()
): ResolvedChartDateRange | null {
  const type = context.dateRangeType;
  if (type && DYNAMIC_RANGE_TYPES.has(type)) {
    const dynamic = computeDynamicRange(type, context.lastNDays, today);
    return dynamic ? capToInsightsLimit(dynamic.start, dynamic.end, today) : null;
  }

  if (context.startDate && context.endDate) {
    const start = dayjs(context.startDate);
    const end = dayjs(context.endDate);
    if (start.isValid() && end.isValid()) {
      return capToInsightsLimit(start, end, today);
    }
  }

  return null;
}

export function contextCanDrilldown(context: ChartQueryContext, today: Dayjs = dayjs()): boolean {
  return Boolean(context.dimension && resolveEffectiveDateRange(context, today));
}

export function unwrapChartRequest(response: any): any {
  const data = response?.data;
  if (
    data &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    (data.dimension ||
      data.Dimension ||
      data.chart_type ||
      data.ChartType ||
      data.filters ||
      data.Filters ||
      data.criteria ||
      data.Criteria)
  ) {
    return data;
  }
  return response;
}

function readRowField(row: any, fieldNames: string[]): string | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const key = Object.keys(row).find((candidate) => fieldNames.includes(candidate.toLowerCase()));
  if (!key) {
    return null;
  }
  const text = String(row[key] ?? '').trim();
  if (!text || text === '-') {
    return null;
  }
  return text;
}

function uniqueTrimmed(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    const text = String(value ?? '').trim();
    const key = text.toLowerCase();
    if (!text || seen.has(key)) {
      return;
    }
    seen.add(key);
    result.push(text);
  });
  return result;
}

function collectSources(root: any): any[] {
  if (!root || typeof root !== 'object') {
    return [];
  }
  const description = root.Description || root.description;
  const nested = root.data;
  const nestedDescription = nested?.Description || nested?.description;
  return [
    root,
    root.config,
    root.criteria,
    root.Criteria,
    description,
    description?.criteria_applied,
    description?.criteria,
    nested,
    nestedDescription,
    nestedDescription?.criteria_applied,
    nestedDescription?.criteria,
    nested?.criteria,
    root.config?.criteria,
  ].filter((source) => source && typeof source === 'object');
}

function readCriteria(sources: any[]): Record<string, unknown> | null {
  for (const source of sources) {
    const criteria = source.criteria || source.Criteria || source.criteria_applied;
    if (criteria && typeof criteria === 'object' && !Array.isArray(criteria)) {
      return criteria as Record<string, unknown>;
    }
    if (
      source.date_range_type ||
      source.dateRangeType ||
      source.last_n_days != null ||
      source.lastNDays != null
    ) {
      return source as Record<string, unknown>;
    }
  }
  return null;
}

function readFilters(sources: any[]): FilterRequest[] {
  for (const source of sources) {
    const filters = source.filters || source.Filters;
    if (!Array.isArray(filters) || filters.length === 0) {
      continue;
    }
    const normalized = filters
      .map((filter) => normalizeStoredFilter(filter))
      .filter((filter): filter is FilterRequest => !!filter);
    if (normalized.length) {
      return normalized;
    }
  }
  return [];
}

function normalizeStoredFilter(filter: any): FilterRequest | null {
  if (!filter || typeof filter !== 'object') {
    return null;
  }
  const columnName = filter.columnName ?? filter.field;
  const operationType = filter.operationType ?? filter.operator;
  if (columnName == null || operationType == null || String(columnName).trim() === '') {
    return null;
  }
  return {
    logicalOperator: filter.logicalOperator || 'AND',
    columnName: String(columnName),
    operationType: String(operationType),
    value: filter.value ?? '',
    isClusterFilter: Boolean(filter.isClusterFilter),
    clusterFilters: Array.isArray(filter.clusterFilters) ? filter.clusterFilters : [],
  };
}

function readLastNDays(criteria: Record<string, unknown> | null, sources: any[]): number | null {
  const raw =
    criteria?.['last_n_days'] ??
    criteria?.['lastNDays'] ??
    firstNumber(sources, ['last_n_days', 'lastNDays', 'LastNDays']);
  const parsed = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
}

function firstString(sources: any[], keys: string[]): string | null {
  for (const source of sources) {
    if (!source) {
      continue;
    }
    for (const key of keys) {
      const value = source[key];
      if (value == null) {
        continue;
      }
      const text = String(value).trim();
      if (text) {
        return text;
      }
    }
  }
  return null;
}

function firstNumber(sources: any[], keys: string[]): number | null {
  for (const source of sources) {
    if (!source) {
      continue;
    }
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
      if (typeof value === 'string' && value.trim() && !Number.isNaN(Number(value))) {
        return Number(value);
      }
    }
  }
  return null;
}

function normalizeDateString(value: string | null): string | null {
  if (!value) {
    return null;
  }
  const date = value.split('T')[0];
  return dayjs(date).isValid() ? dayjs(date).format('YYYY-MM-DD') : null;
}

function normalizeRangeType(value: string | null): string | null {
  if (!value) {
    return null;
  }
  return DURATION_TO_RANGE[value.toLowerCase()] || null;
}

function computeDynamicRange(
  type: string,
  lastNDays: number | null,
  today: Dayjs
): { start: Dayjs; end: Dayjs } | null {
  const current = today.startOf('day');
  switch (type) {
    case 'today':
      return { start: current, end: current };
    case 'this_week':
      return { start: current.startOf('week'), end: current.endOf('week').startOf('day') };
    case 'this_month':
      return { start: current.startOf('month'), end: current.endOf('month').startOf('day') };
    case 'this_quarter': {
      const quarterStartMonth = Math.floor(current.month() / 3) * 3;
      const start = current.month(quarterStartMonth).startOf('month');
      return { start, end: start.add(2, 'month').endOf('month').startOf('day') };
    }
    case 'this_year':
      return { start: current.startOf('year'), end: current.endOf('year').startOf('day') };
    case 'last_n_days': {
      const days = lastNDays && lastNDays > 0 ? lastNDays : 7;
      return { start: current.subtract(days - 1, 'day'), end: current };
    }
    default:
      return null;
  }
}

function capToInsightsLimit(start: Dayjs, end: Dayjs, today: Dayjs = dayjs()): ResolvedChartDateRange | null {
  let rangeStart = start.startOf('day');
  let rangeEnd = end.startOf('day');
  if (!rangeStart.isValid() || !rangeEnd.isValid() || rangeStart.isAfter(rangeEnd, 'day')) {
    return null;
  }

  let capped = false;
  const todayStart = today.startOf('day');
  if (rangeEnd.isAfter(todayStart, 'day') && rangeEnd.diff(rangeStart, 'day') > INSIGHTS_MAX_RANGE_DAYS) {
    rangeEnd = todayStart;
    capped = true;
  }
  if (rangeEnd.diff(rangeStart, 'day') > INSIGHTS_MAX_RANGE_DAYS) {
    rangeStart = rangeEnd.subtract(INSIGHTS_MAX_RANGE_DAYS, 'day');
    capped = true;
  }
  if (rangeStart.isAfter(rangeEnd, 'day')) {
    return null;
  }

  return {
    startDate: rangeStart.format('YYYY-MM-DD'),
    endDate: rangeEnd.format('YYYY-MM-DD'),
    capped,
  };
}
